import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { getTranslation, getTranslationArr, logError } from "../helpers/utils";

/**
 * Normalize organizational_status to internal keys matching the 3 admin options only.
 * Keys: 'government' | 'semi-government' | 'non-government-civil-society'.
 * UI shows two sections: Government | Non-Government (Semi-government + Non Government/ Civil Society).
 */
function normalizeOrgStatusKeyFromText(text) {
  if (!text || typeof text !== "string") return "";
  const t = text.toLowerCase().trim();
  if (t === "government") return "government";
  if (t === "semi-government") return "semi-government";
  if (t === "non government/ civil society") return "non-government-civil-society";
  return "";
}

function normalizeOrgStatusKey(translationsArr) {
  if (!Array.isArray(translationsArr) || translationsArr.length === 0) return "";
  for (let i = 0; i < translationsArr.length; i++) {
    const text = translationsArr[i]?.text || translationsArr[i]?.value || "";
    const key = normalizeOrgStatusKeyFromText(typeof text === "string" ? text : "");
    if (key) return key;
  }
  return "";
}

export const getPoliceServices = createAsyncThunk(
  "OrganizationSlice/getPoliceServices",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/police-services/${data.selDistrictId}`
      );
      return {
        res: response.data.data["Police Services"],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OrganizationSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

export const getMedicalServices = createAsyncThunk(
  "OrganizationSlice/getMedicalServices",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/emergency-services/${data.selDistrictId}`
      );
      return {
        res: response.data.data["Medical Services"],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OrganizationSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

export const getServiceProviders = createAsyncThunk(
  "OrganizationSlice/getServicePrviders",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/service-providers/${data.selDistrictId}`
      );
      return {
        res: response.data.data["Service Providers"],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OrganizationSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllServiceProviders = createAsyncThunk(
  "OrganizationSlice/getAllServiceProviders",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/all-service-providers/${data.selDistrictId}`
      );
      return {
        res: response.data.data["Service Providers"],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OrganizationSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

export const getNationalHelplines = createAsyncThunk(
  "OrganizationSlice/getNationalHelplines",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/national-helplines`
      );
      return {
        res: response.data.data["National Helpline"],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OrganizationSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

export const getProvincialHelplines = createAsyncThunk(
  "OrganizationSlice/getProvincialHelplines",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/provincial-helplines/${data.selProvinceId}`
      );
      return {
        res: response.data.data["Service Providers"],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OrganizationSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

// NEW: Get all offences for referral directory tabs
export const getOffences = createAsyncThunk(
  "OrganizationSlice/getOffences",
  async (data, { rejectWithValue, getState }) => {
    try {
      // Skip if we already have offences cached
      const state = getState();
      if (state.organizations.rawOffences && state.organizations.rawOffences.length > 0) {
        return {
          res: state.organizations.rawOffences,
          langId: data.langId,
          fromCache: true,
        };
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/offences`
      );
      return {
        res: response.data.data?.Offences || response.data.data || [],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OrganizationSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

// NEW: Get organizations by offence with filtering
export const getOrganizationsByOffence = createAsyncThunk(
  "OrganizationSlice/getOrganizationsByOffence",
  async (data, { rejectWithValue, getState }) => {
    try {
      // Create cache key based on offence, province, and district
      const cacheKey = `${data.offenceId}_${data.provinceId || 'all'}_${data.districtId || 'all'}`;
      
      // Check if we already have cached data for this combination
      const state = getState();
      const cachedData = state.organizations.organizationsCache?.[cacheKey];
      if (cachedData) {
        if (process.env.NODE_ENV === 'development') console.log('[OrganizationSlice] Using cached organizations for:', cacheKey);
        return {
          res: cachedData,
          langId: data.langId,
          cacheKey,
          fromCache: true,
        };
      }
      
      const params = new URLSearchParams();
      if (data.provinceId) params.append('province', data.provinceId);
      if (data.districtId) params.append('district', data.districtId);
      
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/organizations-by-offence/${data.offenceId}?${params.toString()}`
      );
      return {
        res: response.data.data || [],
        langId: data.langId,
        cacheKey,
      };
    } catch (error) {
      logError(error, "OrganizationSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

export const OrganizationSlice = createSlice({
  name: "Organization",
  initialState: {
    policeServices: null,
    medicalServices: null,
    serviceProviders: {},
    nationalHelplines: {},
    provincialHelplines: {},
    providers: [],
    selectedItem: {},
    // NEW: For restructured referral directory
    offences: [],
    rawOffences: [], // Cache raw offences to avoid refetching
    organizationsByOffence: [],
    organizationsCache: {}, // Cache organizations per offence/location combination
    selectedOffence: null,
  },
  reducers: {
    getTranslatedPoliceServices: (state, action) => {
      state.policeServices = action.payload.policeServices.map((obj) => {
        let transPoliceSer = { ...obj };
        transPoliceSer.title = getTranslation(obj.title, action.payload.langId);
        transPoliceSer.description = getTranslation(
          obj.description,
          action.payload.langId
        );
        transPoliceSer.service_type = getTranslation(
          obj.service_type,
          action.payload.langId
        );
        let arr = [];
        obj.contact.forEach((con) => {
          arr.push(getTranslation(con, action.payload.langId));
        });
        transPoliceSer.contact = arr;
        return transPoliceSer;
      });
    },
    setSelectedItem: (state, action) => {
      state.selectedItem = action.payload;
    },
    setSelectedOffence: (state, action) => {
      state.selectedOffence = action.payload;
    },
    resetOrgStates: (state, _action) => {
      state.policeServices = null;
      state.medicalServices = null;
      state.serviceProviders = {};
      state.nationalHelplines = {};
      state.provincialHelplines = {};
      state.providers = [];
      state.selectedItem = {};
      state.offences = [];
      state.organizationsByOffence = [];
      state.selectedOffence = null;
    },
    // When only location (province/district) changes: clear org list so it refetches; keep offences list
    clearOrganizationsForLocation: (state) => {
      state.organizationsByOffence = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPoliceServices.fulfilled, (state, action) => {
        state.policeServices = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          trans.service_type = getTranslation(
            obj.service_type,
            action.payload.langId
          );
          trans.address = getTranslationArr(obj.address, action.payload.langId);
          trans.contact = getTranslationArr(obj.contact, action.payload.langId);
          return trans;
        });
      })
      .addCase(getPoliceServices.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getMedicalServices.fulfilled, (state, action) => {
        state.medicalServices = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          trans.service_type = getTranslation(
            obj.service_type,
            action.payload.langId
          );
          trans.contact = getTranslationArr(obj.contact, action.payload.langId);
          trans.address = getTranslationArr(obj.address, action.payload.langId);
          return trans;
        });
      })
      .addCase(getMedicalServices.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getServiceProviders.fulfilled, (state, action) => {
        const items = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          trans.service_type = getTranslation(
            obj.service_type,
            action.payload.langId
          );
          trans.address = getTranslationArr(obj.address, action.payload.langId);
          trans.contact = getTranslationArr(obj.contact, action.payload.langId);
          return trans;
        });
        state.providers = items;
        const groupedData = {};
        items?.forEach((item) => {
          if (!groupedData[item.service_type]) {
            groupedData[item.service_type] = [item];
          } else {
            groupedData[item.service_type].push(item);
          }
        });
        state.serviceProviders = groupedData;
      })
      .addCase(getServiceProviders.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getAllServiceProviders.fulfilled, (state, action) => {
        const items = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          trans.service_type = getTranslation(
            obj.service_type,
            action.payload.langId
          );
          trans.address = getTranslationArr(obj.address, action.payload.langId);
          trans.contact = getTranslationArr(obj.contact, action.payload.langId);
          return trans;
        });
        state.providers = items;
        const groupedData = {};
        items?.forEach((item) => {
          if (!groupedData[item.service_type]) {
            groupedData[item.service_type] = [item];
          } else {
            groupedData[item.service_type].push(item);
          }
        });
        state.serviceProviders = groupedData;
      })
      .addCase(getAllServiceProviders.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getNationalHelplines.fulfilled, (state, action) => {
        const items = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          trans.service_type = getTranslation(
            obj.service_type,
            action.payload.langId
          );
          trans.address = getTranslationArr(obj.address, action.payload.langId);
          trans.contact = getTranslationArr(obj.contact, action.payload.langId);
          return trans;
        });
        const groupedData = {};
        items?.forEach((item) => {
          if (!groupedData[item.service_type]) {
            groupedData[item.service_type] = [item];
          } else {
            groupedData[item.service_type].push(item);
          }
        });
        state.nationalHelplines = groupedData;
      })
      .addCase(getNationalHelplines.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getProvincialHelplines.fulfilled, (state, action) => {
        const items = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          trans.service_type = getTranslation(
            obj.service_type,
            action.payload.langId
          );
          trans.address = getTranslationArr(obj.address, action.payload.langId);
          trans.contact = getTranslationArr(obj.contact, action.payload.langId);
          return trans;
        });
        const groupedData = {};
        items?.forEach((item) => {
          if (!groupedData[item.service_type]) {
            groupedData[item.service_type] = [item];
          } else {
            groupedData[item.service_type].push(item);
          }
        });
        state.provincialHelplines = groupedData;
      })
      .addCase(getProvincialHelplines.rejected, (state, action) => {
        toast.error(action.payload);
      })
      // NEW: Offences for referral directory
      .addCase(getOffences.fulfilled, (state, action) => {
        const raw = action.payload.res || [];
        // Deduplicate by _id so React keys are unique (API may return same offence twice)
        const seen = new Set();
        const deduped = raw.filter((obj) => {
          const id = obj?._id?.toString?.() || obj?._id;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        if (process.env.NODE_ENV === 'development') console.log("[OrganizationSlice] Received offences:", deduped.length, raw.length !== deduped.length ? `(deduped from ${raw.length})` : "", action.payload.fromCache ? "(from cache)" : "");
        
        // Store raw offences (no translation here) so UI can translate by current language
        if (!action.payload.fromCache) {
          state.rawOffences = deduped;
        }
        state.offences = deduped.map((obj) => ({ ...obj }));
      })
      .addCase(getOffences.rejected, (state, action) => {
        logError(action.payload, "OrganizationSlice: getOffences");
      })
      // NEW: Organizations by offence
      .addCase(getOrganizationsByOffence.fulfilled, (state, action) => {
        // Response: government, semiGovernment, nonGovernmentCivilSociety, nonGovernment (3 admin options only)
        let allOrgs = [];
        const res = action.payload.res;
        
        if (process.env.NODE_ENV === 'development') console.log("[OrganizationSlice] organizations-by-offence response:", res);
        
        if (Array.isArray(res)) {
          allOrgs = res;
        } else {
          allOrgs = [
            ...(res.government || []),
            ...(res.semiGovernment || []),
            ...(res.nonGovernmentCivilSociety || []),
          ];
        }
        // Deduplicate by _id so we never render the same org twice (avoids React key warnings)
        const seenIds = new Set();
        allOrgs = allOrgs.filter((obj) => {
          const id = obj?._id?.toString?.() || obj?._id;
          if (seenIds.has(id)) return false;
          seenIds.add(id);
          return true;
        });
        
        if (process.env.NODE_ENV === 'development') console.log("[OrganizationSlice] Total orgs found:", allOrgs.length);
        
        state.organizationsByOffence = allOrgs.map((obj) => {
          let trans = { ...obj };
          // Handle title - could be Translation array, object with translations, or string
          if (Array.isArray(obj.title)) {
            trans.title = getTranslation(obj.title, action.payload.langId);
            if (trans.title === "Translation N/A" && obj.title[0]?.text) {
              trans.title = obj.title[0].text;
            }
          } else if (obj.title?.translations) {
            trans.title = getTranslation(obj.title.translations, action.payload.langId);
            if (trans.title === "Translation N/A") trans.title = obj.title?.text || trans.title;
          } else if (obj.title?.text) {
            trans.title = obj.title.text;
          } else if (typeof obj.title === 'string') {
            trans.title = obj.title;
          }
          // Handle description
          if (Array.isArray(obj.description)) {
            trans.description = getTranslation(obj.description, action.payload.langId);
            if (trans.description === "Translation N/A" && obj.description[0]?.text) {
              trans.description = obj.description[0].text;
            }
          } else if (obj.description?.translations) {
            trans.description = getTranslation(obj.description.translations, action.payload.langId);
            if (trans.description === "Translation N/A") trans.description = obj.description?.text || trans.description;
          } else if (obj.description?.text) {
            trans.description = obj.description.text;
          }
          // Handle service_type
          if (Array.isArray(obj.service_type)) {
            trans.service_type = getTranslation(obj.service_type, action.payload.langId);
            if (trans.service_type === "Translation N/A" && obj.service_type[0]?.text) {
              trans.service_type = obj.service_type[0].text;
            }
          } else if (obj.service_type?.translations) {
            trans.service_type = getTranslation(obj.service_type.translations, action.payload.langId);
          } else if (obj.service_type?.text) {
            trans.service_type = obj.service_type.text;
          }
          // organizational_status: 3 admin options → _orgStatusKey for Government | Non-Government grouping
          if (Array.isArray(obj.organizational_status)) {
            trans.organizational_status = getTranslation(obj.organizational_status, action.payload.langId);
            if (trans.organizational_status === "Translation N/A" && obj.organizational_status[0]?.text) {
              trans.organizational_status = obj.organizational_status[0].text;
            }
            // Normalized key for filtering: 'government' | 'semi-government' | 'non-government-civil-society' (3 admin options only)
            trans._orgStatusKey = normalizeOrgStatusKey(obj.organizational_status);
          } else if (obj.organizational_status?.translations) {
            trans.organizational_status = getTranslation(obj.organizational_status.translations, action.payload.langId);
            trans._orgStatusKey = normalizeOrgStatusKey(obj.organizational_status.translations);
          } else if (obj.organizational_status?.text) {
            trans.organizational_status = obj.organizational_status.text;
            trans._orgStatusKey = normalizeOrgStatusKeyFromText(obj.organizational_status.text);
          } else if (typeof obj.organizational_status === 'string') {
            trans.organizational_status = obj.organizational_status;
            trans._orgStatusKey = normalizeOrgStatusKeyFromText(obj.organizational_status);
          } else {
            trans._orgStatusKey = '';
          }
          // Handle arrays
          trans.address = obj.address?.length ? getTranslationArr(obj.address, action.payload.langId) : [];
          trans.contact = obj.contact?.length ? getTranslationArr(obj.contact, action.payload.langId) : [];
          return trans;
        });
        
        // Cache the raw response for future use (only if not from cache)
        if (!action.payload.fromCache && action.payload.cacheKey) {
          state.organizationsCache[action.payload.cacheKey] = action.payload.res;
        }
      })
      .addCase(getOrganizationsByOffence.rejected, (state, action) => {
        logError(action.payload, "OrganizationSlice: getOrganizationsByOffence");
      });
  },
});
export const { getTranslatedPoliceServices, setSelectedItem, setSelectedOffence, resetOrgStates, clearOrganizationsForLocation } =
  OrganizationSlice.actions;

export default OrganizationSlice.reducer;
